import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Building2, Target, Monitor, Users, ShieldCheck, BarChart2, Globe, PieChart, Settings, Activity, ChevronDown, LogOut, BookOpen, Tags, Hash, GitCompare } from 'lucide-react';
import planiqIcon from '../assets/icon.png';

const ACCENT = '#0090d0';
const C = {
  sidebar: 'rgba(255,255,255,0.75)',
  border: '#dce8f0',
  text: '#1a2a3a',
  text2: '#4a6a8a',
  text3: '#8aa0b8',
  bg: 'linear-gradient(135deg, #e8eef5 0%, #dce6f0 40%, #e4ecf5 70%, #d8e8f2 100%)',
};

// First letter of first + last name (e.g. "Dorel Samoila" -> "DS"); first two
// letters of the single name if there's no space to split on.
function getInitials(fullName, email) {
  const name = (fullName || '').trim();
  if (name) {
    const parts = name.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (email || '?').slice(0, 2).toUpperCase();
}

function TopBar({ user, logout }) {
  const [open, setOpen] = useState(false);
  if (!user) return <div style={{ height: 52, flexShrink: 0, background: '#eef4f8', borderBottom: `1px solid ${C.border}` }} />;

  const displayName = user.full_name || user.email;
  const initials = getInitials(user.full_name, user.email);

  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 20, flexShrink: 0,
      height: 52, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
      padding: '0 24px',
      background: '#eef4f8', // solid, not translucent — sticky header over scrolling content
      borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{ position: 'relative' }}>
        <button onClick={() => setOpen(o => !o)} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: '5px 8px', borderRadius: 8,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
            background: `linear-gradient(135deg, ${ACCENT}, #0060a0)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 800, fontSize: 12,
            boxShadow: '0 2px 6px rgba(0,120,200,0.25)',
          }}>
            {initials}
          </div>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayName}
          </span>
          <ChevronDown size={14} color={C.text3} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>

        {open && (
          <>
            <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 29 }} />
            <div style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 230,
              background: '#fff', borderRadius: 10, border: `1px solid ${C.border}`,
              boxShadow: '0 4px 20px rgba(80,120,160,0.12)', zIndex: 30, overflow: 'hidden',
            }}>
              <div style={{ padding: '12px 14px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {displayName}
                </div>
                <div style={{ fontSize: 11, color: C.text3, marginTop: 3 }}>
                  {user.org_name} · {user.role?.replace('_', ' ')}
                </div>
              </div>
              <div style={{ borderTop: `1px solid ${C.border}` }} />
              <button
                onClick={logout}
                style={{
                  width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: 13, color: C.text2,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = `${ACCENT}10`; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <LogOut size={14} /> Sign Out
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout, isAdmin, isManager, hasSites, canManageTaxonomy } = useAuth();
  const location = useLocation();
  const isPlanEditor = /\/plans\/\d+/.test(location.pathname);
  const [sidebarVisible, setSidebarVisible] = useState(!isPlanEditor);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => { setSidebarVisible(!isPlanEditor); }, [isPlanEditor]);

  const role = user?.role;
  const orgType = user?.org_type || 'agency';
  const isAgency = orgType === 'agency';

  const canSeeUsers = ['super_admin','admin'].includes(role);
  const canSeeClients = isAgency && ['super_admin','admin','account_manager','senior_planner','junior_planner'].includes(role);
  const canSeeBrands = !isAgency || ['super_admin','admin','senior_planner','junior_planner','client_view','brand_view'].includes(role);

  const navStyle = ({ isActive }) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 12px', borderRadius: 8,
    textDecoration: 'none', fontSize: 13, fontWeight: 500,
    color: isActive ? ACCENT : C.text2,
    background: isActive ? `${ACCENT}15` : 'transparent',
    borderLeft: isActive ? `2px solid ${ACCENT}` : '2px solid transparent',
    transition: 'all .15s',
  });

  const canSeeCatalogue = ['super_admin','admin','senior_planner'].includes(role);

  const canSeeOverview = !isAgency && ['super_admin','admin','senior_planner','client_view'].includes(role);

  const navItems = [
    { to: '/', end: true, icon: LayoutDashboard, label: 'Dashboard' },
    ...(isAgency && canSeeCatalogue ? [{ to: '/agency-summary', icon: PieChart, label: 'Agency Summary' }] : []),
    ...(canSeeClients ? [{ to: '/clients', icon: Building2, label: 'Clients' }] : []),
    ...(!isAgency && canSeeBrands ? [{ to: '/brands', icon: Target, label: 'Brands' }] : []),
    ...(canSeeOverview ? [{ to: '/overview', icon: BarChart2, label: 'Account Overview' }] : []),
    ...(isManager ? [{ to: '/diagnostic', icon: Activity, label: 'Strategic Diagnostic' }] : []),
    ...(canSeeCatalogue ? [
      { to: '/platforms', icon: Monitor, label: 'Platforms & Formats' },
      ...(hasSites ? [{ to: '/sites', icon: Globe, label: 'Sites' }] : []),
      { to: '/audiences', icon: Target, label: 'Audiences' },
      { to: '/trackers', icon: Tags, label: 'Trackers' },
    ] : []),
    ...(canManageTaxonomy ? [{ to: '/taxonomy', icon: Hash, label: 'Taxonomy' }] : []),
    { to: '/compare', icon: GitCompare, label: 'Compare Plans' },
    ...(canSeeUsers ? [{ to: '/users', icon: Users, label: 'Users' }] : []),
    ...(role==='super_admin' ? [{ to: '/admin', icon: ShieldCheck, label: 'Admin Panel' }] : []),
    { to: '/settings', icon: Settings, label: 'Settings' },
    { to: '/user-guide', icon: BookOpen, label: 'User Guide' },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: C.bg, position: 'relative' }}>
      {!sidebarVisible && (
        <button onClick={() => setSidebarVisible(true)} style={{
          position: 'fixed', top: 56, left: 0, zIndex: 100,
          width: 20, height: 48, background: ACCENT, border: 'none',
          borderRadius: '0 6px 6px 0', cursor: 'pointer', color: '#fff', fontSize: 12,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '2px 0 8px rgba(0,120,200,0.2)',
        }}>›</button>
      )}

      {sidebarVisible && (
        <div style={{
          width: collapsed ? 52 : 220, flexShrink: 0,
          background: C.sidebar, backdropFilter: 'blur(12px)',
          borderRight: `1px solid ${C.border}`,
          display: 'flex', flexDirection: 'column',
          transition: 'width .2s', overflow: 'hidden',
          boxShadow: '2px 0 16px rgba(80,120,160,0.08)',
        }}>
          {/* Logo */}
          <div style={{ padding: '16px 12px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={planiqIcon} alt="PlanIQ" style={{ width: 34, height: 34, flexShrink: 0, objectFit: 'contain' }} />
            {!collapsed && (
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.3px', lineHeight: 1 }}>
                  <span style={{ color: C.text }}>Plan</span><span style={{ color: ACCENT }}>IQ</span>
                </div>
                <div style={{ color: C.text3, fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', marginTop: 2 }}>planiq.live</div>
              </div>
            )}
          </div>

          {/* Nav */}
          <nav style={{ flex: 1, padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {navItems.map(({ to, end, icon: Icon, label }) => (
              <NavLink key={to} to={to} end={end} style={navStyle}>
                <Icon size={18}/>
                {!collapsed && <span>{label}</span>}
              </NavLink>
            ))}
          </nav>

          {/* Sidebar collapse toggle — user info/sign-out now live in the top bar */}
          <div style={{ padding: '10px 8px', borderTop: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => setCollapsed(c => !c)} style={{ flex: 1, padding: '6px 10px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 6, color: C.text3, cursor: 'pointer', fontSize: 12 }}>
                {collapsed ? '→' : '←'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar user={user} logout={logout} />
        <Outlet />
      </div>
    </div>
  );
}
